import { useEffect, useRef } from 'react';
import { symbolImage } from '@shared/symbols';
import { useT } from '../i18n';
import type { Token } from '../lib/sentence';
import { Pictogram } from './Pictogram';

const actionIcon = (icon: string, file: string) => (
  <Pictogram source={{ icon, image: symbolImage(file) }} className="action__icon" />
);

interface Props {
  tokens: Token[];
  draft: string;
  onSpeak(): void;
  onDeleteWord(): void;
  onClearAll(): void;
  onRemoveToken(index: number): void;
  onOpenSettings(): void;
  speaking?: boolean;
}

/**
 * The sentence being built plus the main actions. By design it has exactly two
 * delete actions: "Apagar palavra" (last word) and "Apagar tudo" (everything).
 * Deleting single letters only exists on the keyboard.
 */
export function SentenceBar({ tokens, draft, onSpeak, onDeleteWord, onClearAll, onRemoveToken, onOpenSettings }: Props) {
  const t = useT();
  const stripRef = useRef<HTMLOListElement>(null);
  const empty = tokens.length === 0 && !draft.trim();

  // Keep the newest word visible.
  useEffect(() => {
    const strip = stripRef.current;
    if (strip) strip.scrollLeft = strip.scrollWidth;
  }, [tokens.length, draft]);

  return (
    <section className="sentence-bar" aria-label={t('sentenceLabel')}>
      <div className="sentence" aria-live="polite">
        {empty ? (
          <p className="sentence__placeholder">{t('sentenceEmpty')}</p>
        ) : (
          <ol className="sentence__tokens" ref={stripRef}>
            {tokens.map((token, i) => (
              <li key={token.key}>
                <button
                  type="button"
                  className="token"
                  onClick={() => onRemoveToken(i)}
                  aria-label={t('removeFromSentence', { word: token.text })}
                >
                  {token.picto && <Pictogram source={token.picto} className="token__picto" />}
                  <span>{token.text}</span>
                </button>
              </li>
            ))}
            {draft.trim() && (
              <li>
                <span className="token token--draft">{draft.trim()}</span>
              </li>
            )}
          </ol>
        )}
      </div>
      <div className="sentence-actions">
        <button type="button" className="action action--speak" onClick={onSpeak} disabled={empty}>
          {actionIcon('🔊', 'ui-speak.webp')} {t('speak')}
        </button>
        <button type="button" className="action" onClick={onDeleteWord} disabled={tokens.length === 0}>
          {actionIcon('⌫', 'ui-delete-word.webp')} {t('deleteWord')}
        </button>
        <button type="button" className="action" onClick={onClearAll} disabled={empty}>
          {actionIcon('🗑️', 'ui-clear.webp')} {t('clearAll')}
        </button>
        {/* Opens the caregiver area (PIN protected). */}
        <button type="button" className="action action--settings" onClick={onOpenSettings} aria-label={t('adultArea')}>
          {actionIcon('⚙️', 'ui-settings.webp')} <span className="action__text">{t('adjust')}</span>
        </button>
      </div>
    </section>
  );
}
